import React, { useEffect, useState } from 'react';
import { FaPlus, FaEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../assets/clients.css';
import Loader from '../components/Loader';
import { useAuth } from '../contexts/AuthContext';

// Data-protection restriction: only these users may export the clients table.
const CLIENTS_EXPORT_ALLOWED_EMAILS = [
  'stuart.woolgar+sm@global-guardians.co.uk',
  'lauren.boswell@global-guardians.co.uk',
  'vikaila.vekaria@global-guardians.co.uk',
];

// The client pipeline boxes are ordered by the `order` column on client_statuses,
// so the sequence is controlled from the data and not hardcoded here.
// Any status without an order is pushed to the end rather than hidden.
const getPipelineOrder = (status) =>
  Number.isFinite(Number(status?.order)) ? Number(status.order) : Number.MAX_SAFE_INTEGER;

const SortIcon = ({ direction, isActive }) => (
  <svg width="10" height="16" viewBox="0 0 10 16" style={{ marginLeft: 4, verticalAlign: 'middle' }}>
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date)) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const Clients = () => {
  const { user } = useAuth();
  const canExportClients =
    !!user?.email &&
    (CLIENTS_EXPORT_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase()) || user.email.trim().toLowerCase().endsWith('@kimonservices.com'));

  const [clients, setClients] = useState([]);
  const [clientStatuses, setClientStatuses] = useState([]);
  const [clientCount, setClientCount] = useState({});
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate state for input value
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load vs search/filter
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedStatusCard, setSelectedStatusCard] = useState('');
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [emailStatusFilter, setEmailStatusFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0); // Total count for overview card

  // Column visibility state (matching Laravel - Phone, Email, VIP Amount, Savings Made, Last Updated hidden by default)
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    status: true,
    web: true,
    manager_first: true,
    manager_last: true,
    lead_source: true,
    market_segment: true,
    bank_account: true,
    phone: false, // Hidden by default
    email: false, // Hidden by default
    vip_amount: false, // Hidden by default
    savings_made: false, // Hidden by default
    last_updated: false, // Hidden by default
    bank_details: false, // Hidden by default
    address: false, // Hidden by default
    address_1: false, // Hidden by default
    address_2: false, // Hidden by default
    address_3: false, // Hidden by default
    town: false, // Hidden by default
    postcode: false, // Hidden by default
    internal_info: false, // Hidden by default
    actions: true
  });

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);

  const navigate = useNavigate();

  // Column mapping for server-side sorting (index kept for DataTables compatibility,
  // the field name is what the backend actually resolves)
  const getSortColumnIndex = (field) => {
    const mapping = {
      "name": 0,              // client.name
      "email": 1,             // client.email
      "phone": 2,             // client.phone_number
      "status": 3,            // status.name
      "last_updated": 4,      // client.updated_at
      "web": 5,               // client.web
      "manager_first": 6,     // manager.first_name
      "manager_last": 7,      // manager.last_name
      "lead_source": 8,       // leadSource.name
      "market_segment": 9,    // marketSegment.name
      "bank_account": 10,     // bankAccount.name
      "vip_amount": 11,       // client.vip_amount
      "savings_made": 12,     // client.savings_made
      "bank_details": 13,     // concatenated bank details
      "address": 14,          // concatenated full address
      "address_1": 15,        // client.address_1
      "address_2": 16,        // client.address_2
      "address_3": 17,        // client.address_3
      "town": 18,             // client.town
      "postcode": 19,         // client.postcode
      "internal_info": 20,    // client.internal_info
    };
    return mapping[field] || 0;
  };

  // Debounce search input to avoid triggering API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch clients when search, filters, or pagination changes
  useEffect(() => {
    fetchClients();
  }, [currentPage, pageSize, search, sortField, sortDirection, statusFilter]);

  // Close column dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnDropdown && !event.target.closest('.column-visibility-container')) {
        setShowColumnDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnDropdown]);

  const fetchClients = async () => {
    // Set loading state (but only show full-page loader on initial load)
    setLoading(true);

    const datatableParams = {
      draw: draw,
      start: (currentPage - 1) * pageSize,
      length: pageSize,
      search: { value: search },
      order: [{ column: getSortColumnIndex(sortField), name: sortField, dir: sortDirection }],
      statusFilter: statusFilter && statusFilter !== 'total' ? statusFilter : '',
    };

    console.log("📊 [FRONTEND] Fetching clients with params:", datatableParams);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/clients/datatable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(datatableParams)
      });

      const data = await response.json();
      console.log("✅ [FRONTEND] Received clients data:", data);

      setClients(data.data || []);
      setTotalRecords(data.recordsFiltered || 0);
      setDraw(data.draw);

      // Update status counts if provided
      if (data.clientStatuses) {
        setClientStatuses(data.clientStatuses);
      }
      if (data.clientCount) {
        setClientCount(data.clientCount);
        // Set total count for overview card
        setTotalCount(data.clientCount.total || data.recordsTotal || 0);
      } else {
        // Fallback to recordsTotal if clientCount not provided
        setTotalCount(data.recordsTotal || 0);
      }

      setLoading(false);
      setIsInitialLoad(false); // Mark initial load as complete
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
      setLoading(false);
      setIsInitialLoad(false); // Mark initial load as complete even on error
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Show opens the client in a new browser tab (Laravel parity)
  const handleShow = (id) => window.open(`/clients/${id}`, '_blank', 'noopener,noreferrer');
  const handleEdit = (id) => navigate(`/clients/${id}/edit`);
  const handleCreate = () => navigate('/clients/create');

  const handleStatusCardClick = (statusName) => {
    console.log('🔍 Client status card clicked:', statusName);
    if (statusName === 'total') {
      setStatusFilter('');
      setSelectedStatusCard('total');
    } else {
      // Find status ID from status name
      const status = clientStatuses.find(s => s.name === statusName);
      const statusId = status ? status.id.toString() : '';
      const newFilter = statusFilter === statusId ? '' : statusId;
      setStatusFilter(newFilter);
      setSelectedStatusCard(newFilter ? statusName : '');
    }
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleRemove = (id) => {
    if (window.confirm('Are you sure you want to remove this client?')) {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      fetch(`${backendUrl}/api/clients/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
        .then(res => {
          if (res.ok) {
            fetchClients(); // Refresh data after deletion
          } else {
            alert('Failed to delete client');
          }
        });
    }
  };

  // Column visibility toggle
  const toggleColumn = (column) => {
    setVisibleColumns(prev => {
      const next = { ...prev, [column]: !prev[column] };
      // Hiding the column we're sorted by would leave the list in an order the
      // user can no longer see or undo - fall back to the default sort
      if (!next[column] && sortField === column) {
        setSortField("name");
        setSortDirection("asc");
        setCurrentPage(1);
      }
      return next;
    });
  };

  // Export handlers
  const handleExportCSV = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/clients/export/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          statusFilter: statusFilter && statusFilter !== 'total' ? statusFilter : '',
          search: { value: search }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      // Get the CSV blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      console.log('✅ CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV file');
    }
  };

  const handleExportPDF = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/clients/export/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          statusFilter: statusFilter && statusFilter !== 'total' ? statusFilter : '',
          search: { value: search }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to export PDF');
      }

      // Get the PDF blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-export-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      console.log('✅ PDF exported successfully');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF file');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleColumnVisibility = () => {
    setShowColumnDropdown(!showColumnDropdown);
  };

  // Only show full-page loader on initial load
  if (loading && isInitialLoad) return <Loader />;

  return (
    <div className="container-fluid clients-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-row">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-clients">Clients</span>
      </div>

      {/* Status Overview */}
      <div className="clients-overview-row">
        {[...clientStatuses]
          .sort((a, b) => getPipelineOrder(a) - getPipelineOrder(b) || String(a.name).localeCompare(String(b.name)))
          .map(status => {
          const isActive = selectedStatusCard === status.name || statusFilter === status.id.toString();
          return (
            <div 
              key={status.id} 
              className={`overview-card ${isActive ? 'active' : ''}`}
              style={{ 
                backgroundColor: status.color || '#f8f9fa',
                cursor: 'pointer',
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                border: isActive ? '3px solid #fff' : 'none',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleStatusCardClick(status.name)}
              title={`Filter by ${status.name} status`}
            >
              <div className="overview-value">{clientCount[status.name] || 0}</div>
              <div className="overview-label">{status.name}</div>
            </div>
          );
        })}
        <div 
          className={`overview-card total ${selectedStatusCard === 'total' ? 'active' : ''}`}
          style={{
            cursor: 'pointer',
            transform: selectedStatusCard === 'total' ? 'scale(1.05)' : 'scale(1)',
            border: selectedStatusCard === 'total' ? '3px solid #fff' : 'none',
            transition: 'all 0.3s ease'
          }}
          onClick={() => handleStatusCardClick('total')}
          title="Show all clients"
        >
          <div className="overview-value">{totalCount}</div>
          <div className="overview-label">Total</div>
        </div>
      </div>

      {/* Email Link Section */}
      <div className="email-link-row">
        <div className="email-status-group">
          <span style={{ fontWeight: 'bold', marginRight: 10 }}>Email client of status:</span>
          <select
            className="form-control email-status-select"
            value={emailStatusFilter}
            onChange={(e) => {
              const statusId = e.target.value;
              setEmailStatusFilter(statusId);
              if (statusId) {
                navigate(`/clients/emails/status/${statusId}`);
              } else {
                navigate('/clients/emails');
              }
            }}
            style={{ display: 'inline-block', width: 'auto', marginRight: 10 }}
          >
            <option value="">Please select</option>
            {clientStatuses.map(status => (
              <option key={status.id} value={status.id}>{status.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Clients Table Panel */}
      <div className="clients-table-panel">
        <div className="clients-table-header-row">
          <div className="clients-table-title">Clients</div>
          <div className="clients-header-buttons">
            <button className="btn btn-add-client" onClick={handleCreate}>
              <FaPlus style={{ marginRight: 8 }} /> Add client
            </button>
            <button className="btn btn-email ml-2" onClick={() => navigate('/clients/emails')}>
              <FaEnvelope style={{ marginRight: 8 }} /> Email All
            </button>
            <button className="btn btn-email-status ml-2" onClick={() => navigate('/clients/emails/status/create')}>
              <FaEnvelope style={{ marginRight: 8 }} /> Email clients of status
            </button>
          </div>
        </div>
        <div className="clients-table-toolbar">
          <div className="clients-table-entries">
            Show
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
              {[10, 25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            entries
          </div>
          <div className="clients-table-export-buttons">
            {canExportClients && (
              <>
                <button className="btn btn-default btn-sm" onClick={handleExportCSV}>
              <i className="glyphicon glyphicon-download"></i> Export as CSV
            </button>
            <button className="btn btn-default btn-sm" onClick={handleExportPDF}>
              <i className="glyphicon glyphicon-file"></i> Export as PDF
            </button>
              </>
            )}
            <button className="btn btn-default btn-sm" onClick={handlePrint}>
              <i className="glyphicon glyphicon-print"></i> Print table
            </button>
            <div className="column-visibility-container" style={{ position: 'relative', display: 'inline-block' }}>
              <button className="btn btn-default btn-sm dropdown-toggle" onClick={handleColumnVisibility}>
                <i className="glyphicon glyphicon-eye-open"></i> Column visibility
                <span className="caret"></span>
              </button>
              {showColumnDropdown && (
                <div className="column-visibility-menu" style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '200px',
                  padding: '10px',
                  marginTop: '5px'
                }}>
                  {Object.entries(visibleColumns).map(([column, visible]) => (
                    <div key={column} className="column-visibility-item" style={{ marginBottom: '5px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={() => toggleColumn(column)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ textTransform: 'capitalize' }}>
                          {column.replace(/_/g, ' ')}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="clients-table-search">
            Search:
            <input
              type="text"
              className="form-control"
              placeholder=""
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <div className="clients-table-wrapper" style={{ position: 'relative' }}>
          {/* Show subtle loading indicator during search/filter (not initial load) */}
          {loading && !isInitialLoad && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              pointerEvents: 'none'
            }}>
              <div style={{ fontSize: '16px', color: '#333' }}>Loading...</div>
            </div>
          )}
          <table className="clients-table" style={{ opacity: loading && !isInitialLoad ? 0.5 : 1 }}>
            <thead>
              <tr>
                <th 
                  className="sortable"
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  Status<SortIcon direction={sortField === "status" ? sortDirection : null} isActive={sortField === "status"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("web")}
                  style={{ cursor: "pointer" }}
                >
                  Web<SortIcon direction={sortField === "web" ? sortDirection : null} isActive={sortField === "web"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("manager_first")}
                  style={{ cursor: "pointer" }}
                >
                  Manager First<SortIcon direction={sortField === "manager_first" ? sortDirection : null} isActive={sortField === "manager_first"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("manager_last")}
                  style={{ cursor: "pointer" }}
                >
                  Manager Last<SortIcon direction={sortField === "manager_last" ? sortDirection : null} isActive={sortField === "manager_last"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("lead_source")}
                  style={{ cursor: "pointer" }}
                >
                  Lead Source<SortIcon direction={sortField === "lead_source" ? sortDirection : null} isActive={sortField === "lead_source"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("market_segment")}
                  style={{ cursor: "pointer" }}
                >
                  Market Segment<SortIcon direction={sortField === "market_segment" ? sortDirection : null} isActive={sortField === "market_segment"} />
                </th>
                <th 
                  className="sortable"
                  onClick={() => handleSort("bank_account")}
                  style={{ cursor: "pointer" }}
                >
                  Bank Account<SortIcon direction={sortField === "bank_account" ? sortDirection : null} isActive={sortField === "bank_account"} />
                </th>
                {visibleColumns.phone && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("phone")}
                    style={{ cursor: "pointer" }}
                  >
                    Phone<SortIcon direction={sortField === "phone" ? sortDirection : null} isActive={sortField === "phone"} />
                  </th>
                )}
                {visibleColumns.email && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("email")}
                    style={{ cursor: "pointer" }}
                  >
                    Email<SortIcon direction={sortField === "email" ? sortDirection : null} isActive={sortField === "email"} />
                  </th>
                )}
                {visibleColumns.vip_amount && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("vip_amount")}
                    style={{ cursor: "pointer" }}
                  >
                    VIP Amount<SortIcon direction={sortField === "vip_amount" ? sortDirection : null} isActive={sortField === "vip_amount"} />
                  </th>
                )}
                {visibleColumns.savings_made && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("savings_made")}
                    style={{ cursor: "pointer" }}
                  >
                    Savings Made<SortIcon direction={sortField === "savings_made" ? sortDirection : null} isActive={sortField === "savings_made"} />
                  </th>
                )}
                {visibleColumns.bank_details && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("bank_details")}
                    style={{ cursor: "pointer" }}
                  >
                    Bank Details<SortIcon direction={sortField === "bank_details" ? sortDirection : null} isActive={sortField === "bank_details"} />
                  </th>
                )}
                {visibleColumns.address && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("address")}
                    style={{ cursor: "pointer" }}
                  >
                    Address<SortIcon direction={sortField === "address" ? sortDirection : null} isActive={sortField === "address"} />
                  </th>
                )}
                {visibleColumns.address_1 && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("address_1")}
                    style={{ cursor: "pointer" }}
                  >
                    Address 1<SortIcon direction={sortField === "address_1" ? sortDirection : null} isActive={sortField === "address_1"} />
                  </th>
                )}
                {visibleColumns.address_2 && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("address_2")}
                    style={{ cursor: "pointer" }}
                  >
                    Address 2<SortIcon direction={sortField === "address_2" ? sortDirection : null} isActive={sortField === "address_2"} />
                  </th>
                )}
                {visibleColumns.address_3 && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("address_3")}
                    style={{ cursor: "pointer" }}
                  >
                    Address 3<SortIcon direction={sortField === "address_3" ? sortDirection : null} isActive={sortField === "address_3"} />
                  </th>
                )}
                {visibleColumns.town && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("town")}
                    style={{ cursor: "pointer" }}
                  >
                    Town<SortIcon direction={sortField === "town" ? sortDirection : null} isActive={sortField === "town"} />
                  </th>
                )}
                {visibleColumns.postcode && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("postcode")}
                    style={{ cursor: "pointer" }}
                  >
                    Postcode<SortIcon direction={sortField === "postcode" ? sortDirection : null} isActive={sortField === "postcode"} />
                  </th>
                )}
                {visibleColumns.internal_info && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("internal_info")}
                    style={{ cursor: "pointer" }}
                  >
                    Internal info<SortIcon direction={sortField === "internal_info" ? sortDirection : null} isActive={sortField === "internal_info"} />
                  </th>
                )}
                {visibleColumns.last_updated && (
                  <th 
                    className="sortable"
                    onClick={() => handleSort("last_updated")}
                    style={{ cursor: "pointer" }}
                  >
                    Last Updated<SortIcon direction={sortField === "last_updated" ? sortDirection : null} isActive={sortField === "last_updated"} />
                  </th>
                )}
                {visibleColumns.actions && <th className="action-head">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {clients.map((client, idx) => (
                <tr key={client.id || idx}>
                  <td>
                    <a 
                      href="#" 
                      className="text-primary client-link" 
                      onClick={e => { e.preventDefault(); navigate(`/clients/${client.id}`); }}
                    >
                      {client.name}
                    </a>
                  </td>
                  <td>
                    {client.status ? (
                      <span 
                        className="status-badge" 
                        style={{ 
                          backgroundColor: client.status.color || '#6c757d',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.85em'
                        }}
                      >
                        {client.status.name}
                      </span>
                    ) : '-'}
                  </td>
                  <td>
                    {client.web ? (
                      <a href={client.web} target="_blank" rel="noopener noreferrer">
                        {client.web}
                      </a>
                    ) : '-'}
                  </td>
                  <td>{client.manager?.first_name || '-'}</td>
                  <td>{client.manager?.last_name || '-'}</td>
                  <td>{client.leadSource?.name || '-'}</td>
                  <td>{client.marketSegment?.name || '-'}</td>
                  <td>{client.bankAccount?.name || '-'}</td>
                  {visibleColumns.phone && (
                    <td>{client.phone_number || '-'}</td>
                  )}
                  {visibleColumns.email && (
                    <td>{client.email || '-'}</td>
                  )}
                  {visibleColumns.vip_amount && (
                    <td>
                      {client.vip_amount ? (
                        <span className="amount">£{parseFloat(client.vip_amount).toLocaleString()}</span>
                      ) : '-'}
                    </td>
                  )}
                  {visibleColumns.savings_made && (
                    <td>
                      {client.savings_made ? (
                        <span className="amount">£{parseFloat(client.savings_made).toLocaleString()}</span>
                      ) : '-'}
                    </td>
                  )}
                  {visibleColumns.bank_details && (
                    <td>{client.bankDetails || '-'}</td>
                  )}
                  {visibleColumns.address && (
                    <td>{client.fullAddress || '-'}</td>
                  )}
                  {visibleColumns.address_1 && (
                    <td>{client.address_1 || '-'}</td>
                  )}
                  {visibleColumns.address_2 && (
                    <td>{client.address_2 || '-'}</td>
                  )}
                  {visibleColumns.address_3 && (
                    <td>{client.address_3 || '-'}</td>
                  )}
                  {visibleColumns.town && (
                    <td>{client.town || '-'}</td>
                  )}
                  {visibleColumns.postcode && (
                    <td>{client.postcode || '-'}</td>
                  )}
                  {visibleColumns.internal_info && (
                    <td>{client.internal_info || '-'}</td>
                  )}
                  {visibleColumns.last_updated && (
                    <td>{formatDate(client.updated_at)}</td>
                  )}
                  {visibleColumns.actions && (
                    <td className="actions-cell">
                    <a
                      className="btn-action show"
                      href={`/clients/${client.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Show"
                    >
                      Show
                      {/* <FaEye /> */}
                    </a>
                    <button 
                      className="btn-action edit" 
                      onClick={() => handleEdit(client.id)}
                      title="Edit"
                    >
                      Edit
                      {/* <FaEdit /> */}
                    </button>
                    <button 
                      className="btn-action remove" 
                      onClick={() => handleRemove(client.id)}
                      title="Remove"
                    >
                      Remove
                      {/* <FaTrash /> */}
                    </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {clients.length === 0 && (
          <div className="no-clients-message">
            No clients found!
          </div>
        )}

        {/* Pagination Controls */}
        {totalRecords > pageSize && (
          <div className="pagination-container" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              First
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              Previous
            </button>

            <span style={{ padding: '5px 10px' }}>
              Page {currentPage} of {Math.ceil(totalRecords / pageSize)}
            </span>

            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
              style={{ padding: '5px 10px' }}
            >
              Next
            </button>
            <button
              className="btn btn-sm"
              onClick={() => setCurrentPage(Math.ceil(totalRecords / pageSize))}
              disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
              style={{ padding: '5px 10px' }}
            >
              Last
            </button>

            <span style={{ marginLeft: '15px', fontSize: '14px' }}>
              ({totalRecords} total clients)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients; 